/**
 * Copyright (c) [2015-2019] SUSE Linux
 *
 * This software may be modified and distributed under the terms
 * of the MIT license.  See the LICENSE.txt file for details.
 */
/* eslint-disable indent */

import { Janus } from '../vendor/janus';
import { createRoomFromJanus } from './models/room';
import { createFeedConnection } from './models/feed-connection';
import StreamsService from "../utils/streams-service";
import { apiCreateCallRecord } from '../api/CallRecord';

/**
 * Builds an object to interact with a Janus server
 *
 * @param {Object} config Janus config options
 * @property {String | Array<String>} config.janusServer Janus server URL. It can be an array with several URLs.
 * @property {Boolean | String | Array<String>} config.janusDebug Debug parameter for Janus.init()
 * @property {Boolean} config.janusWithCredentials Whether to set credentials in XHR requests
 * @property {Integer} config.joinUnmutedLimit Feeds limit to connect as unmuted
 * @property {Boolean} config.videoThumbnails Whether to use video thumbnails by default
 * @returns {Object}
 */
export const createRoomService = (
  config,
  feedsService,
  dataChannelService,
  eventsService,
  actionService
) => {
  const { janusServer, janusDebug, janusWithCredentials, joinUnmutedLimit, videoThumbnails } = config;
  const createFeedConnectionFactory = createFeedConnection(eventsService);
  let startMuted = false;

  let that = {
    room: null,
    pin: null,
    privateId: null,
    withCredentials: !!janusWithCredentials,
    recordplay: null,
    playHandle: null
  };
  that.server = janusServer;

  /**
   * Connects to the Janus server
   *
   * @returns {Promise}
   */
  that.connect = () => {
    return new Promise(function(resolve, reject) {
      if (that.janus) {
        resolve(true);
      } else {
        Janus.init({debug: janusDebug});
        console.log(that.server);
        const jn = new Janus({
          server: that.server,
          withCredentials: that.withCredentials,
          success: () => {
            that.janus = jn;
            resolve(true);
          },
          error: (e) => {
            // TODO: move this to a better place
            const msg = `Janus error: ${e}. Do you want to reload in order to retry?"`;
            reject();
            if (window.confirm(msg)) {
              window.location.reload();
            }
          },
          destroyed: () => console.log('Janus object destroyed')
        });
      }
    });
  };



  /*
   * Creates a new room
   */
  that.createRoom = (params) => {
    return new Promise((resolve) => {
      that.connect().then(() => {
        that.doCreateRoom(params).then((res) => resolve(res));
      });
    });
  };
  that.doCreateRoom = (params) => {
    return new Promise((resolve, reject) => {
      that.janus.attach({
        plugin: 'janus.plugin.videoroom',
        error: (error) => console.error(error),
        success: function (pluginHandle) {
          const request = { request: 'create', ...params };
          pluginHandle.send({
            message: request,
            success: function (result) {
              // Free the resource (it looks safe to do it here)
              pluginHandle.detach();
              resolve(result);
            }
          });
        }
      });
    });
  };


  /**
   * destroyed room
   */
  that.destroyedRoom = (params) => {
    return new Promise((resolve) => {
      that.connect().then(() => {
        that.doDestroyedRoom(params).then((res) => resolve(res));
      });
    });
  };

  that.doDestroyedRoom = (params) => {
    return new Promise((resolve, reject) => {
      //console.log('params======', params);
      that.janus.attach({
        plugin: 'janus.plugin.videoroom',
        error: (error) => console.error(error),
        success: function (pluginHandle) {
          const request = { request: 'destroy', ...params };
          pluginHandle.send({
            message: request,
            success: function (result) {
              // Free the resource (it looks safe to do it here)
              //console.log('result======Here', result);
              pluginHandle.detach();
              resolve(result);
            }
          });
        }
      });
    });
  };


  /**
   * Returns the list of rooms from the server
   *
   * This function makes sures that a connection already exists, but the real work is done by the
   * doGetRooms function.
   *
   * @returns {Promise<Array>} List of rooms from the server
   */
  that.getRooms = () => {
    return new Promise((resolve) => {
      that.connect().then(() => {
        that.doGetRooms().then((rooms) => resolve(rooms));
      });
    });
  };

  /**
   * Returns the list of rooms from the server
   *
   * @todo This function should be private.
   *
   * @returns {Promise<Array>} List of rooms from the server
   */
  that.doGetRooms = () => {
    return new Promise((resolve, reject) => {
      that.janus.attach({
        plugin: 'janus.plugin.videoroom',
        error: (error) => console.error(error),
        success: function(pluginHandle) {
          console.log(
            'getAvailableRooms plugin attached (' +
              pluginHandle.getPlugin() +
              ', id=' +
              pluginHandle.getId() +
              ')'
          );
          const request = { request: 'list' };
          pluginHandle.send({
            message: request,
            success: function(result) {
              // Free the resource (it looks safe to do it here)
              pluginHandle.detach();
              if (result.videoroom === 'success') {
                let rooms = result.list.map((r) => createRoomFromJanus(r));
                resolve(rooms);
              } else {
                reject();
              }
            }
          });
        }
      });
    });
  };


  // record
  that.record = (mixStreams, callId, roomId) => {
    return new Promise((resolve, reject) => {
      that.connect().then(function () {
        that.doRecord(mixStreams, callId, roomId).then((res) => resolve(res));
      });
    });
  };

  that.doRecord = (mixStreams, callId, roomId) => {
    return new Promise((resolve, reject) => {
      // sending Record  event
      eventsService.auditEvent('record');
      // Create new session
      that.janus.attach({
        plugin: 'janus.plugin.recordplay',
        success: function (pluginHandle) {
          that.recordplay = pluginHandle;
          console.log("Recordplay Plugin attached! (" + that.recordplay.getPlugin() + ", id=" + that.recordplay.getId() + ")");

          pluginHandle.send({
            message: {
              request: 'configure',
              'video-bitrate-max': 1024 * 1024,		// a quarter megabit
              'video-keyframe-interval': 15000	// 15 seconds
            }
          });
   
          let tracks = [];
          if (mixStreams) {
            console.log("mixStreams for recording:", mixStreams);
            const audioTracks = mixStreams.getAudioTracks();
            console.log("mixStreams audio tracks count:", audioTracks.length);
             
            if (audioTracks.length > 0) {
              let captureAudio = audioTracks[0];
              console.log("captureAudio track:", captureAudio);
              console.log("captureAudio enabled:", captureAudio.enabled);
              console.log("captureAudio readyState:", captureAudio.readyState);
              console.log("captureAudio muted:", captureAudio.muted);
              console.log("captureAudio kind:", captureAudio.kind);
               
              // 检查轨道是否有效
              if (captureAudio.enabled && captureAudio.readyState === 'live' && !captureAudio.muted) {
                tracks.push({ type: 'audio', capture: captureAudio, recv: false });
                console.log("Using provided audio track for recording");
              } else {
                console.log("Audio track not valid, using default capture");
                tracks.push({ type: 'audio', capture: true, recv: false });
              }
            } else {
              console.log("No audio tracks in mixStreams, using default capture");
              tracks.push({ type: 'audio', capture: true, recv: false });
            }
          } else {
            console.log("No mixStreams, using default audio capture");
            tracks.push({ type: 'audio', capture: true, recv: false });
          }
        
          console.log("tracks init====", tracks);

          pluginHandle.createOffer({
            // tracks: [
            //   { type: 'audio', capture: true, recv: false },
            //   { type: 'video', capture: true, recv: false, simulcast: false },
            //   //~ { type: 'data' },
            // ],
            tracks: tracks,
            success: function (jsep) {
              const request = { 
                request: "record", 
                name: 'recording-' + Date.now(), 
                audio: true, 
                video: false,
                opusred: true,
                talking_events: false
              };
              pluginHandle.send({
                message: request,
                jsep: jsep,
                success: function (result) {
                  console.log('record back data=====', result);
                  resolve(result);
                }
              });
            },
            error: function (error) {
              console.log("record WebRTC error...........error", error.message);
              reject(error);
              pluginHandle.hangup();
            }
          });

        },
        error: function (error) {
          console.error('Record Error attaching plugin... ' + error);
          reject(error);
        },
        onmessage: function (msg, jsep) {
          console.log(" ::: Got a message :::", msg);
          let result = msg["result"];
          if (result) {
            if (result["status"]) {
              let event = result["status"];
              if (event === 'preparing' || event === 'refreshing') {
                console.log("Preparing the recording playout");
                that.recordplay.createAnswer(
                  {
                    jsep: jsep,
                    // We only specify data channels here, as this way in
                    // case they were offered we'll enable them. Since we
                    // don't mention audio or video tracks, we autoaccept them
                    // as recvonly (since we won't capture anything ourselves)
                    tracks: [
                      { type: 'data' }
                    ],
                    success: function (jsep) {
                      console.log("Got SDP!", jsep);
                      let body = { request: "start" };
                      that.recordplay.send({ message: body, jsep: jsep });
                    },
                    error: function (error) {
                      console.log("WebRTC error:", error);
                    }
                  });
                if (result["warning"])
                  console.log("warning===========", result["warning"]);
              } else if (event === 'recording') {
                // Got an ANSWER to our recording OFFER
                if (jsep)
                  that.recordplay.handleRemoteJsep({ jsep: jsep });
                let id = result["id"];
                if (id) {
                  console.log("The ID of the current recording is " + id);
                  // 创建录音记录
                  if (callId && roomId) {
                    console.log("Creating call record with videoId:", id, "callId:", callId, "roomId:", roomId);
                    try {
                      apiCreateCallRecord({
                        videoId: id,
                        callId: callId,
                        roomId: roomId
                      })
                        .then(res => {
                          console.log("Call record created successfully:", res);
                        })
                        .catch(err => {
                          console.error("Failed to create call record:", err);
                        });
                    } catch (err) {
                      console.error("Error creating call record:", err);
                    }
                  } else {
                    console.warn("Missing callId or roomId for recording, skipping call record creation");
                  }
                }
              } else if (event === 'stopped') {
                console.log("Session has stopped!");
                // useRecordStore.setState((state) => ({
                //   ...state,
                //   recording: false

                // }));
                if (that.recordplay) {
                  that.recordplay.hangup();
                }
              } else if (event === 'done') {
                console.log("done!");
                // useRecordStore.setState((state) => ({
                //   ...state,
                //   recording: false
                // }));
              }
            }
          }
          //console.log('msg=======start', msg);
          //console.log('jsep=======start', jsep);
        }
      });
    });
  };

  /**
  * recordStop
  */
  that.recordStop = () => {
    return new Promise((resolve, reject) => {
      that.connect().then(function () {
        that.doRecordStop().then((res) => resolve(res));
      });
    });
  };

  that.doRecordStop = () => {
    return new Promise((resolve, reject) => {
      const request = { request: "stop" };
      if (that.recordplay) {
        that.recordplay.send({
          message: request,
          success: function (result) {
            resolve(result);
          },
          error: function (error) {
            reject(error);
          }
        });
        that.recordplay.hangup();
        that.recordplay = null;
      } else {
        reject('Recordplay Plugin not attached');
      }
    });
  };


  // recordPlay
  that.play = (id) => {
    return new Promise((resolve, reject) => {
      that.connect().then(function () {
        that.doPlay(id).then((res) => resolve(res))
      });
    });
  };

  that.doPlay = (id) => {
    return new Promise((resolve, reject) => {
      // 确保 id 是数字
      const videoIdNum = parseInt(id, 10);
      if (isNaN(videoIdNum) || videoIdNum <= 0) {
        reject(new Error(`Invalid video ID: ${id}`));
        return;
      }
      
      let audioStreamId = null;
      // Create new session
      that.janus.attach({
        plugin: 'janus.plugin.recordplay',
        success: function (pluginHandle) {
          that.playHandle = pluginHandle;
          console.log("Recordplay Plugin attached! (" + that.playHandle.getPlugin() + ", id=" + that.playHandle.getId() + ")");
           const request = { request: "play", id: videoIdNum };
          pluginHandle.send({
            message: request,
            success: function (result) {
              console.log('back play data========', result);
              resolve(result);
            }
          })
        },
        error: function (error) {
          console.error('Record Error attaching plugin... ' + error);
          reject(error);
        },
        onmessage: function (msg, jsep) {
          console.log(" ::: Got a message :::", msg);
          let result = msg["result"];
          if (result) {
            if (result["status"]) {
              let event = result["status"];
              if (event === 'preparing' || event === 'refreshing') {
                console.log("Preparing the recording playout");
                that.playHandle.createAnswer(
                  {
                    jsep: jsep,
                    // We only specify data channels here, as this way in
                    // case they were offered we'll enable them. Since we
                    // don't mention audio or video tracks, we autoaccept them
                    // as recvonly (since we won't capture anything ourselves)
                    tracks: [
                      { type: 'data' }
                    ],
                    success: function (jsep) {
                      console.log("Got SDP!", jsep);
                      let body = { request: "start" };
                      that.playHandle.send({ message: body, jsep: jsep });
                    },
                    error: function (error) {
                      console.log("WebRTC error:", error);
                    }
                  });
                if (result["warning"])
                  console.log("warning===========", result["warning"]);
              } else if (event === 'stopped') {
                console.log("Session has stopped!");
                
                if (that.playHandle) {
                  that.playHandle.hangup();
                }
              } else if (event === 'playing') {
                console.log("Playout has started!");
              } else if (event === 'done') {
                console.log("done!");
                
              }
            }
          }

        },
        onremotetrack: function (track, mid, on, metadata) {
          if (track.kind === "audio") {
            let stream = new MediaStream([track]);
            StreamsService.add(stream.id, stream);
            // useRecordStore.setState((state) => ({
            //   ...state,
            //   playing: true,
            //   audioStreamId: stream.id
            // }));
            console.log("Created remote audio stream:", stream);
            console.log("Created remote audio stream2:", stream.id);
            // Store stream ID for cleanup
            audioStreamId = stream.id;
            that.playAudioStreamId = stream.id;
            // Emit event for UI to know audio stream is ready
            eventsService.roomEvent('audioStreamReady', { videoId: videoIdNum, streamId: stream.id });
          } 
        },
        oncleanup: function () {
          console.log(" ::: Got a cleanup notification :::");
          // Clean up stored audio stream
          if (audioStreamId) {
            StreamsService.delete(audioStreamId);
            audioStreamId = null;
          }
          if (that.playAudioStreamId) {
            that.playAudioStreamId = null;
          }
          // Emit event for UI
          eventsService.roomEvent('audioStreamCleaned', { videoId: videoIdNum });
          // useRecordStore.setState((state) => ({
          //   ...state,
          //   playing: false,
          //   audioStreamId: '',
          //   videoStreamId: ''
          // }));
        }
      });
    });
  };

  // recordPlayStop
  that.stopPlay = () => {
    return new Promise((resolve, reject) => {
      that.connect().then(function () {
        that.doStopPlay().then((res) => resolve(res))
      });
    });
  };

  that.doStopPlay = () => {
    return new Promise((resolve, reject) => {
      const request = { request: "stop" };
      if (that.playHandle) {
        that.playHandle.send({
          message: request,
          success: function (result) {
            resolve(result);
          },
          error: function (error) {
            reject(error)
          }
        })
        that.playHandle.hangup();
        that.playHandle = null;
      } else {
        reject('playHandle Plugin not attached');
      }
    });
  };

  // change record traks
  that.changeTracks = (tracks) => {
    const handle = that.recordplay;
    console.log("change record traks== start1");
    console.log("change record traks== start2", tracks);
    //console.log("change record traks== start3", handle.webrtcStuff.pc.getTransceivers());
    handle.replaceTracks({
      tracks: tracks,
      error: function (err) {
        //console.log("err====changeCamera", err);
        alert("err====change record traks==" + err)
      }
    });
  };

  // Enter the room
  that.enter = (username, pin) => {
    return new Promise((resolve, reject) => {
      that.connect().then(function() {
        that.doEnter(username, pin, resolve, reject);
      });
    });
  };

  that.doEnter = (username, pin, resolve, reject) => {
    let connection = null;
    that.pin = pin;

    // sending user joining event
    eventsService.auditEvent('user');

    // send user joining event
    // Create new session
    that.janus.attach({
      plugin: 'janus.plugin.videoroom',
      success: function(pluginHandle) {
        // sending 'pluginHandle attached' event
        eventsService.auditEvent('pluginHandle');

        // Step 1. Right after attaching to the plugin, we send a request to join
        connection = createFeedConnectionFactory(pluginHandle, that.room.id, 'main');
        connection.register(username, pin);
      },
      error: function(error) {
        console.error('Error attaching plugin... ' + error);
      },
      consentDialog: function(on) {
        console.log('Consent dialog should be ' + (on ? 'on' : 'off') + ' now');
        eventsService.roomEvent('consentDialog', { on });
        if (!on) {
          //notify if joined muted
          if (startMuted) {
            eventsService.roomEvent(
              'muteFeed',
              { id: feedsService.findMain().id, participantsLimit: joinUnmutedLimit }
            );
          }
        }
      },
      ondataopen: function() {
        console.log('The publisher DataChannel is available');
        connection.onDataOpen();
        that.sendStatus();
      },
      onlocaltrack: function(track, _on) {
        // Step 4b (parallel with 4a).
        // Send the created stream to the UI, so it can be attached to
        // some element of the local DOM
        let feed = feedsService.findMain();
        feed.addTrack(track);
        emitStreamEvents(feed);
      },
      oncleanup: function() {
        console.log(' ::: Got a cleanup notification: we are unpublished now :::');
      },
      onmessage: function(msg, jsep) {
        let event = msg.videoroom;
        console.debug('Event: ' + event);

        // Step 2. Response from janus confirming we joined
        if (event === 'joined') {
          console.log('Successfully joined room ' + msg.room);
          resolve(true);
          // sending user joined event
          eventsService.auditEvent('user');

          that.privateId = msg.private_id;
          actionService.enterRoom(msg.id, username, connection);
          // Step 3. Establish WebRTC connection with the Janus server

          // Step 4a (parallel with 4b). Publish our feed on server

          if (isPresent(joinUnmutedLimit)) {
            startMuted =
              isArray(msg.publishers) && msg.publishers.length >= joinUnmutedLimit;
          }

          connection.publish({
            muted: startMuted,
            error: function() {
              connection.publish({ noCamera: true, muted: startMuted });
            }
          });

          // Step 5. Attach to existing feeds, if any
          if (isArray(msg.attendees)) {
            that.addFeeds(msg.attendees, false);
          }
          if (isArray(msg.publishers)) {
            that.addFeeds(msg.publishers, true);
          }
          // The room has been destroyed
        } else if (event === 'destroyed') {
          console.log('The room has been destroyed!');
          eventsService.roomEvent('destroyRoom', {});
        } else if (event === 'event') {
          // Any new feed to attach to?
          if (isArray(msg.publishers)) {
            that.addFeeds(msg.publishers, true);
          }
          // Any new non-publishing attendee?
          if (isPresent(msg.joining)) {
            that.addFeed(msg.joining.id, msg.joining.display, false);
          }
          // One of the publishers has gone away?
          if (isPresent(msg.leaving)) {
            let leaving = msg.leaving;
            actionService.destroyFeed(leaving);
          }
          // One of the publishers has unpublished?
          if (isPresent(msg.unpublished)) {
            let unpublished = msg.unpublished;
            actionService.unpublishFeed(unpublished);
          }
          // Reply to a configure request
          if (msg.configured) {
            connection.confirmConfig();
            let feed = feedsService.findMain();
            eventsService.roomEvent('updateFeed', { id: feed.id, ...feed.getStatus() });
          }
          // The server reported an error
          if (isPresent(msg.error)) {
            console.log('Error message from server', msg.error);
            reject(msg.error);
            eventsService.roomEvent('reportError', { error: msg.error });
          }
        }

        if (isPresent(jsep)) {
          connection.handleRemoteJsep(jsep);
        }
      }
    });
  };

  that.leave = function leave() {
    actionService.leaveRoom();
    that.pin = null;
  };

  that.setRoom = function(room) {
    that.room = room;
  };

  that.getRoom = function() {
    return that.room;
  };

  that.addFeeds = function(list, subscribe) {
    if (list.length === 0) { return }

    console.debug('Got a list of available feeds (' + subscribe + '):', list);
    for (let f = 0; f < list.length; f++) {
      let id = list[f].id;
      let display = list[f].display;
      that.addFeed(id, display, subscribe);
    }
  };

  that.addFeed = function(id, display, subscribe) {
    let feed = feedsService.find(id);

    if (subscribe) {
      if (feed === null || feed.waitingForConnection()) {
        this.subscribeToFeed(id, display);
      }
    } else {
      if (feed === null) {
        actionService.remoteJoin(id, display, null);
      }
    }
  };

  that.subscribeToFeed = function(id, display) {
    let feed = feedsService.find(id);
    let connection = null;

    if (feed) {
      display = feed.display;
    }

    // emit 'subscribe' event
    eventsService.auditEvent('subscriber');

    that.janus.attach({
      plugin: 'janus.plugin.videoroom',
      success: function(pluginHandle) {
        // emit subscriber plugin attached event
        eventsService.auditEvent('pluginHandle');
        connection = createFeedConnectionFactory(pluginHandle, that.room.id, 'subscriber');
        connection.listen(id, that.pin, that.privateId);
      },
      error: function(error) {
        console.error('  -- Error attaching plugin... ' + error);
      },
      onmessage: function(msg, jsep) {
        let event = msg.videoroom;
        if (event === 'attached') {
          // Subscriber created and attached
          // emit 'subscriber attached' event
          eventsService.auditEvent('subscriber');

          // TODO: is the timeout needed?
          window.setTimeout(function() {
            if (feed) {
              actionService.connectToFeed(id, connection);
            } else {
              actionService.remoteJoin(id, display, connection);
            }
            console.log(
              'Successfully attached to feed ' + id + ' (' + display + ') in room ' + msg.room
            );
          });
        } else if (msg.configured) {
          connection.confirmConfig();
        } else if (msg.started) {
          // Initial setConfig, needed to complete all the initializations
          connection.setConfig({ values: { audio: true, data: true, video: !!videoThumbnails } });
        } else if (msg.error_code === 428) {
          console.log("We tried to subscribe to a feed that is not publishing (an attendee)");
          actionService.stopIgnoringFeed(id);
        } else {
          console.log('What has just happened?!', msg);
        }

        if (isPresent(jsep)) {
          connection.subscribe(jsep);
        }
      },
      onremotetrack: function(track, _on) {
        feedsService.waitFor(id).then(feed => {
          feed.addTrack(track);
          emitStreamEvents(feed);
        })
          .catch(console.error);
      },
      ondataopen: function() {
        console.log('The subscriber DataChannel is available');
        connection.onDataOpen();
        // Send status information of all our feeds to inform the newcommer
        that.sendStatus();
      },
      ondata: function(data) {
        dataChannelService.receiveMessage(data, id);
      },
      oncleanup: function() {
        console.log(' ::: Got a cleanup notification (remote feed ' + id + ') :::');
      }
    });
  };

  that.publishScreen = function(videoSource) {
    let feed = feedsService.findMain();
    let display = feed.display;
    let connection;
    let id;

    // emit `screenshare` event
    eventsService.auditEvent('screenshare');

    that.janus.attach({
      plugin: 'janus.plugin.videoroom',
      success: function(pluginHandle) {
        // emit screenshare plugin attached event
        eventsService.auditEvent('pluginHandle');
        connection = createFeedConnectionFactory(pluginHandle, that.room.id, videoSource);
        connection.register(display, that.pin);
        // TODO: ScreenShareService.setInProgress(true);
      },
      error: function(error) {
        console.error('  -- Error attaching screen plugin... ' + error);
      },
      onlocaltrack: function(track, _on) {
        let feed = feedsService.find(id);
        feed.addTrack(track);
        emitStreamEvents(feed);
        eventsService.auditEvent('screenshare');
      },
      oncleanup: function() {
        eventsService.auditEvent('screenshareStop');
        // TODO: ScreenShareService.setInProgress(false);
      },
      onmessage: function(msg, jsep) {
        console.debug(' ::: Got a message (screen) :::', msg);
        let event = msg.videoroom;

        if (event === 'joined') {
          id = msg.id;
          actionService.publishScreen(id, display, connection);

          connection.publish({
            success: function() {
              // TODO: ScreenShareService.setInProgress(false);
            },
            error: function(error) {
              console.log(error);
              unPublishFeed(id);
              // TODO
              // ScreenShareService.setInProgress(false);
              // ScreenShareService.showHelp();
            }
          });
          // Reply to a configure request
        } else if (msg.configured) {
          connection.confirmConfig();
          eventsService.roomEvent('updateFeed', { id: feed.id, ...feed.getStatus() });
        } else {
          console.log('Unexpected event for screen', msg);
        }
        if (isPresent(jsep)) {
          connection.handleRemoteJsep(jsep);
        }
      }
    });
  };

  that.unPublishFeed = function(feedId) {
    return unPublishFeed(feedId);
  };

  function unPublishFeed(feedId) {
    actionService.destroyFeed(feedId);
  }

  that.ignoreFeed = function(feedId) {
    actionService.ignoreFeed(feedId);
  };

  that.reconnectFeed = function(feedId) {
    that.subscribeToFeed(feedId);
  };

  that.toggleChannel = function(type, feed) {
    actionService.toggleChannel(type, feed);
  };

  /**
   * Broadcast status information of all our feeds when a data channel is
   * established.
   *
   * To increase the chances of the info to be received, it sends the most
   * important information right away and the whole status some seconds after.
   * Hacky and dirty, we know.
   */
  that.sendStatus = function() {
    feedsService.publisherFeeds().forEach(function(p) {
      dataChannelService.sendStatus(p, { exclude: 'picture' });
      window.setTimeout(function() {
        dataChannelService.sendStatus(p);
      }, 4000);
    });
  };

  /**
   * Check whether the given argument has a value.
   */
  function isPresent(value) {
    return (value !== undefined && value !== null);
  };

  /**
   * Check whether the given argument is an Array.
   */
  function isArray(value) {
    return (value instanceof Array);
  };

  /**
   * Registers the stream related events
   *
   * @param {object} feed - Feed to inform about
   */
  function emitStreamEvents(feed) {
    eventsService.roomEvent('updateStream', { feedId: feed.id, stream: feed.getStream() });
    eventsService.auditEvent('stream');
  }

  return that;
};
